import { Link, NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart3, ListChecks, LogOut, MessageSquare, PlusCircle } from 'lucide-react';
import { hasAdminAuthority } from '@nbh/shared';
import { useAuth } from '../auth/AuthProvider';
import { BrandMark } from './BrandMark';
import { IdentityCheck } from './IdentityCheck';
import { OutboxInspector } from './dev/OutboxInspector';
import { NotificationBell } from './NotificationBell';
import { SyncStatusChip } from './SyncStatusChip';
import { SyncToast } from './SyncToast';

/**
 * App frame: a thin header for identity and sync state, and a bottom tab bar.
 *
 * Navigation sits at the bottom because the field constraint in CLAUDE.md is
 * one-handed use — the top of a 6" phone is out of thumb reach.
 */
export function AppShell() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const isManager = hasAdminAuthority(user?.role);

  return (
    <div className="flex min-h-dvh flex-col bg-slate-950 text-slate-100">
      {/*
        The brand bar (D28). Forest green ground with a gold rule under it — the one
        place the palette is used as identity rather than as an accent, because a header
        is what the brand guide is actually about.

        White on this green measures 8.13:1 and the gold rule 8.21:1 against the page
        below it, so the bar is legible in a plant room rather than merely correct in a
        brand deck. The rule is a gradient rather than a flat fill — brass catches
        light unevenly, a flat line does not — reused as the one recurring motif
        instead of a new accent invented per surface (also on the login hero and
        StatTile, via the same `border-image` technique). The fill is the same
        top-lit gradient as the login hero now too, for the same reason: a flat
        colour reads as a status bar, and this is the one place the property's own
        identity is on screen.

        BrandMark carries the icon half of the lockup a name in small type alone
        was missing — hospitality headers and mature product headers both put a
        mark beside the wordmark rather than leaning on text alone.
      */}
      <header className="flex items-center justify-between gap-2 border-b-2 border-transparent bg-gradient-to-b from-brookfields-green-light via-brookfields-green to-brookfields-green-dark py-3 pl-[calc(1rem+var(--safe-left))] pr-[calc(1rem+var(--safe-right))] pt-[calc(0.75rem+var(--safe-top))] [border-image:linear-gradient(to_right,var(--color-brookfields-gold-dark),var(--color-brookfields-gold-light),var(--color-brookfields-gold-dark))_1]">
        {/* The identity block is the way in to "which phones can reach my account".
            Tapping who you are is where somebody looks for that, and it costs no space
            in a header that has none to spare — the mark stays small here on purpose:
            a labelled Sign-out button (CLAUDE.md's own accessibility call, not mine to
            spend space on a bigger badge) is what the rest of this row has to fit. */}
        <Link to="/settings/notifications" className="flex min-w-0 items-center gap-2 no-underline">
          <BrandMark size="size-7" />
          <div className="min-w-0">
            <p className="truncate font-brand text-sm font-semibold tracking-tight text-white">
              {t('app.name')}
            </p>
            <p className="truncate text-xs text-brookfields-gold-light">{user?.email}</p>
          </div>
        </Link>
        <div className="flex shrink-0 items-center gap-2">
          <NotificationBell />
          <SyncStatusChip />
          {/*
            Labelled, not an icon alone, and 48px rather than 44 (CLAUDE.md's floor).
            This is the control a shared handset depends on staff finding — with gloves
            on, having been trained once — and a bare glyph is not findable by someone
            who does not already know what it means. The word costs header width, which
            the identity block absorbs: it is `min-w-0 truncate` for exactly this.
          */}
          <button
            type="button"
            onClick={() => void signOut()}
            className="flex min-h-[48px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-transparent px-3 text-sm font-medium text-brookfields-gold-light"
          >
            <LogOut className="size-5" aria-hidden="true" />
            {t('nav.signOut')}
          </button>
        </div>
      </header>

      {/* Above everything, because it asks a question about everything below it. */}
      <IdentityCheck />

      <SyncToast />

      {/*
        pb leaves room for the fixed tab bar so content is never trapped beneath it.
        A flex column as well, so a screen that needs to fill the visible area — the
        message thread, whose composer must sit above the bar — can say `flex-1`
        instead of `min-h-dvh`, which overshoots by exactly this padding.
      */}
      <main className="flex flex-1 flex-col pb-[var(--nav-h)] pl-[var(--safe-left)] pr-[var(--safe-right)]">
        <Outlet />
      </main>

      <OutboxInspector />

      <nav
        aria-label={t('nav.label')}
        className="fixed inset-x-0 bottom-0 flex min-h-[var(--nav-h)] border-t border-slate-800 bg-slate-950/95 pb-[var(--safe-bottom)] pl-[var(--safe-left)] pr-[var(--safe-right)] backdrop-blur"
      >
        <Tab
          to="/"
          icon={<ListChecks className="size-5" aria-hidden="true" />}
          label={t('nav.queue')}
          end
        />
        <Tab
          to="/report"
          icon={<PlusCircle className="size-5" aria-hidden="true" />}
          label={t('nav.report')}
        />
        <Tab
          to="/messages"
          icon={<MessageSquare className="size-5" aria-hidden="true" />}
          label={t('nav.messages')}
        />
        {isManager && (
          <Tab
            to="/admin"
            icon={<BarChart3 className="size-5" aria-hidden="true" />}
            label={t('nav.admin')}
          />
        )}
      </nav>
    </div>
  );
}

function Tab({
  to,
  icon,
  label,
  end,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex min-h-[64px] flex-1 flex-col items-center justify-center gap-1 text-xs font-medium ${
          isActive ? 'text-brookfields-gold' : 'text-slate-400'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}
